// 通道健康页的筛选、聚合与排序（决策 D186）：全在前端，对 /api/channel-health 的快照行做，接口不变。
// 先按条件筛 通道 × 运营商 的原始行，再聚合，最后排序；聚合口径与通道组合详情页相同（mergeStats）。
import type { ChannelState } from "@/ui";
import { mergeStats, opKey, type MemberStats } from "./groupPerformance";
import { healthState, type ChannelHealth } from "./health";
import type { Channel } from "./master";
import { compareNullable } from "./sort";

/** 聚合维度，可多选：channel 通道，mcc MCC，op MCC + MNC（比 mcc 细，同时选时按它）。一个都不选是不聚合（通道 × 运营商）。 */
export type GroupDim = "channel" | "mcc" | "op";

/** 全部聚合维度，也是地址栏里的写法。 */
export const GROUP_DIMS: GroupDim[] = ["channel", "mcc", "op"];

/** 地址栏的 by（逗号分隔）解析成维度，按 GROUP_DIMS 的顺序，认不出的忽略。 */
export function parseGroupBy(by: string): GroupDim[] {
  const xs = by.split(",");
  return GROUP_DIMS.filter((d) => xs.includes(d));
}

/** 地址栏里的筛选条件，空串表示不限。 */
export interface HealthFilters { supplier: string; channel: string; mcc: string; mnc: string; state: string }

/** 按 id 找通道：列表还没加载返回 undefined，加载了找不到（已删除）返回 null。 */
export type ChannelOf = (id: number) => Channel | null | undefined;

/** 表格的一行：一个或几个 通道 × 运营商 合并后的指标。 */
export interface HealthView {
  key: string;
  rows: ChannelHealth[];
  /** 涉及的通道与运营商（"mcc-mnc"），去重，按出现顺序。 */
  channelIds: number[]; ops: string[];
  stats: MemberStats;
  /** 各状态的维度数，按严重程度排（熔断在前）。 */
  states: [ChannelState, number][];
  /** 慢启动受限（TPS 受限）的维度数。 */
  limited: number;
}

/** 状态计数的顺序：要处理的在前。 */
const STATE_ORDER: ChannelState[] = ["open", "degraded", "removed", "disabled", "healthy"];

/** 按条件筛原始行。供应商要从通道取：通道列表还没加载或通道已删除时，选了供应商就不显示。 */
export function filterRows(items: ChannelHealth[], f: HealthFilters, channelOf: ChannelOf): ChannelHealth[] {
  return items.filter((r) => {
    const c = channelOf(r.channel_id);
    return (!f.supplier || c?.supplier_id === Number(f.supplier))
      && (!f.channel || r.channel_id === Number(f.channel))
      && (!f.mcc || r.mcc === f.mcc) && (!f.mnc || r.mnc === f.mnc)
      && (!f.state || healthState(r, c) === f.state);
  });
}

/** 一行所属的组：选中的维度拼起来；不选维度时每个 通道 × 运营商 一组。 */
function groupKey(r: ChannelHealth, dims: GroupDim[]): string {
  if (!dims.length) return `${r.channel_id}:${opKey(r)}`;
  const op = dims.includes("op") ? opKey(r) : dims.includes("mcc") ? r.mcc : null;
  return [dims.includes("channel") ? String(r.channel_id) : null, op].filter((x) => x !== null).join(":");
}

/** 一组行合成一行。 */
function view(key: string, rows: ChannelHealth[], channelOf: ChannelOf): HealthView {
  const counts = new Map<ChannelState, number>();
  rows.forEach((r) => {
    const s = healthState(r, channelOf(r.channel_id));
    counts.set(s, (counts.get(s) ?? 0) + 1);
  });
  return {
    key, rows,
    channelIds: [...new Set(rows.map((r) => r.channel_id))], ops: [...new Set(rows.map(opKey))],
    stats: mergeStats(rows)!,
    states: STATE_ORDER.filter((s) => counts.has(s)).map((s) => [s, counts.get(s)!]),
    limited: rows.filter((r) => r.circuit === "limited").length,
  };
}

/** 按选中的维度合并。快照按通道、mcc、mnc 排好，带通道（或不聚合）时沿用这个顺序；只按运营商聚合时改按 mcc、mnc 排。 */
export function groupRows(rows: ChannelHealth[], dims: GroupDim[], channelOf: ChannelOf): HealthView[] {
  const groups = new Map<string, ChannelHealth[]>();
  rows.forEach((r) => {
    const k = groupKey(r, dims);
    const g = groups.get(k);
    if (g) g.push(r);
    else groups.set(k, [r]);
  });
  const keys = [...groups.keys()];
  if (dims.length && !dims.includes("channel")) keys.sort();
  return keys.map((k) => view(k, groups.get(k)!, channelOf));
}

/** 可排序的列：取排序用的数，没有样本（页面上显示"—"）为 null。 */
export const SORT_COLUMNS = {
  tps: (s: MemberStats) => s.tps,
  "recent.delivery": (s: MemberStats) => (s.recent.sample_size ? s.recent.delivery_rate : null),
  "recent.error": (s: MemberStats) => (s.recent.submit_samples ? s.recent.submit_error_rate : null),
  "hour.delivery": (s: MemberStats) => (s.hour.sample_size ? s.hour.delivery_rate : null),
  "hour.samples": (s: MemberStats) => s.hour.sample_size,
  adjusted: (s: MemberStats) => s.adjusted?.delivery_rate ?? null,
  latency: (s: MemberStats) => s.hour.dlr_latency_ms || null,
} satisfies Record<string, (s: MemberStats) => number | null>;

export type SortKey = keyof typeof SORT_COLUMNS;

/** 当前排序；null 是默认顺序。 */
export type HealthSort = { key: SortKey; desc: boolean } | null;

/** 点列头：先降序，再升序，第三次回到默认顺序。 */
export function nextSort(cur: HealthSort, key: SortKey): HealthSort {
  if (cur?.key !== key) return { key, desc: true };
  return cur.desc ? { key, desc: false } : null;
}

/** 按列排序，没有值的不论升降都在最后；值相同的保持原顺序。 */
export function sortViews(views: HealthView[], sort: HealthSort): HealthView[] {
  if (!sort) return views;
  const value = SORT_COLUMNS[sort.key];
  return [...views].sort((a, b) => compareNullable(value(a.stats), value(b.stats), sort.desc));
}
